import * as React from "react";
import { Box, Grid, Typography, Button, Popover } from "@mui/material";
import { useRouter } from "next/navigation";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CtaButton from "../buttonWithArrow";
import Heading from "../heading";

const MAX_W = 1300;  // Section content cap
const IMG_W = 1300;  // Max width for the illustration

export default function RoiInvestorBenefits({ data, onClick }) {
    const { title, subtitle, button, services, bgcolor } = data;
    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 4 },
                px: { xs: 2, sm: 3 },
                bgcolor: bgcolor ?? "#f7f9f9",
            }}
        >
            {/* Header */}
            <Heading title={title} subtitle={subtitle} mb={7} />

            {/* Service Grid */}
            <Box sx={{ maxWidth: MAX_W, mx: "auto" }}>
                <Grid container spacing={3} justifyContent="center">
                    {services?.map((service, i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                            <ServiceCard
                                image={service?.image}
                                title={service?.name}
                                redirectUrl={service?.href}
                            />
                        </Grid>
                    ))}
                </Grid>

                {/* CTA */}
                {button && (
                    <Box sx={{ mt: 5, textAlign: "center", display: 'flex', justifyContent: 'center' }}>
                        <CtaButton label={button?.text} href={button?.href} />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function ServiceCard({ image, title, redirectUrl }) {
    const router = useRouter();
    const [showComing, setShowComing] = React.useState(false);

    const handleClick = () => {
        if (!redirectUrl) {
            setShowComing(true);

            // Auto-close after 1s
            setTimeout(() => setShowComing(false), 1000);
            return;
        }

        router.push(redirectUrl);
    };

    return (
        <Box sx={{ position: "relative" }}>
            {/* Card */}
            <Box
                onClick={handleClick}
                sx={{
                    borderRadius: "42px",
                    overflow: "hidden",
                    padding: 3,
                    textAlign: "center",
                    backgroundColor: "white",
                    border: "3px solid #000",
                    boxShadow: "4px 4px 0px rgb(48 152 166)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                        transform: "scale(1.05)",
                    },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                }}
            >
                {/* Image */}
                <Box sx={{ position: "relative", height: 80 }}>
                    <img
                        src={image}
                        alt={title}
                        width={60}
                        height={60}
                        style={{ objectFit: "contain" }}
                    />
                </Box>

                {/* Title */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: "22px",
                        lineHeight: "1.4",
                        color: "inherit",
                    }}
                >
                    {title}
                </Typography>

                {/* Button */}
                <Button
                    variant="text"
                    sx={{
                        background: "none",
                        color: "transparent",
                        backgroundImage: "linear-gradient(to right, #75C4C5, #0D8196)",
                        WebkitBackgroundClip: "text",
                        fontWeight: 700,
                        fontSize: "20px",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": {
                            bgcolor: "transparent",
                            textDecoration: "underline",
                        },
                        textTransform: "none",
                    }}
                    onClick={handleClick}
                >
                    Book Now
                    <ArrowForwardIosIcon
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#6cbec1",
                        }}
                    />
                </Button>
            </Box>

            {/* Coming Soon Below Card */}
            {showComing && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-48px", // safer bottom space for grid layouts
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: "#ffffff",
                        color: "#000000",
                        px: 2.5,
                        py: 1,
                        borderRadius: "10px",
                        fontSize: "14px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        boxShadow: "0px 2px 6px rgba(0,0,0,0.12)",   // subtle professional shadow
                        border: "1px solid #e5e7eb",                  // light border for clarity
                        zIndex: 20,
                        animation: "fadeIn 0.25s ease-out",
                        pointerEvents: "none",                        // prevents layout click issues
                    }}
                >
                    Coming Soon
                </Box>

            )}
        </Box>
    );
}


